import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { LicenseManager } from 'ag-grid-enterprise';
import AntdSelectCellRenderer from './AntdSelectCellRenderer';
import AntdDateCellRenderer from './AntdDateCellRenderer';
import AntdSelectCellEditor from './AntdSelectCellEditor';
import AntdDateCellEditor from './AntdDateCellEditor';

// Import AG Grid styles
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

// Import AG Grid modules
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { MultiFilterModule } from '@ag-grid-enterprise/multi-filter';

// Register AG Grid modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MenuModule,
  SetFilterModule,
  StatusBarModule,
  RowGroupingModule,
  MultiFilterModule,
]);

// Set AG Grid Enterprise license key
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_ENTERPRISE_LICENSE);

const AgGridTable = (props) => {
  const {
    rowData,
    columnDefs,
    onCellValueChanged,
    onGridReady,
    defaultColDef: userDefaultColDef,
    statusBar,
    theme = 'ag-theme-material', // Default theme
    ...rest
  } = props;

  const gridRef = useRef();

  // Default column definitions with single-click editing
  const defaultColDef = useMemo(
    () => ({
      minWidth: 150,
      resizable: true,
      filter: true,
      editable: true,
      floatingFilter: true,
      enableValue: true,
      enableRowGroup: true,
      enablePivot: true,
      // Enable single-click editing for all cells including dropdowns
      suppressKeyboardEvent: (params) => false,
      // Ensure single click opens editors
      suppressClickEdit: false,
      // Enable single click for all cell types
      suppressMouseEvent: false,
      // Add single click editing
      singleClickEdit: true,
      // Ensure proper event handling
      suppressCellFocus: false,
      // Enable immediate editing on click
      suppressDoubleClickExpand: true,
      ...userDefaultColDef,
    }),
    [userDefaultColDef]
  );

  // Register custom cell editors and renderers
  const frameworkComponents = useMemo(
    () => ({
      agSelectCellEditor: AntdSelectCellEditor,
      agDateCellEditor: AntdDateCellEditor,
      agSelectCellRenderer: AntdSelectCellRenderer,
      agDateCellRenderer: AntdDateCellRenderer,
    }),
    []
  );

  const handleGridReady = useCallback(
    (params) => {
      console.log('AgGridTable ready, singleClickEdit enabled');
      if (onGridReady) {
        onGridReady(params);
      }
    },
    [onGridReady]
  );

  // Add cell click handler for debugging
  const onCellClicked = useCallback((params) => {
    console.log('Cell clicked:', params.colDef.field, 'Editable:', params.colDef.editable);
    if (params.colDef.editable && params.colDef.cellEditor) {
      console.log('Starting cell editing for:', params.colDef.field);
    }
  }, []);

  return (
    <div
      className={`ag-grid ${theme} custom-grid-theme`}
      style={{ height: '100%', width: '100%' }}
    >
      <AgGridReact
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        frameworkComponents={frameworkComponents}
        onGridReady={handleGridReady}
        onCellValueChanged={onCellValueChanged}
        onRowValueChanged={(params) => {
          console.log('Row value changed:', params.data);
        }}
        onCellClicked={onCellClicked}
        statusBar={statusBar}
        autoHeaderHeight={true}
        rowDragManaged={true}
        animateRows={true}
        rowHeight={40}
        headerHeight={50}
        suppressLoadingOverlay={false}
        suppressNoRowsOverlay={true}
        // Add these props to prevent flickering and ResizeObserver issues
        suppressColumnVirtualisation={false}
        suppressRowVirtualisation={false}
        // Make cells editable on single click
        suppressClickEdit={false}
        // Enable single click editing globally
        singleClickEdit={true}
        // Ensure proper event handling
        suppressCellFocus={false}
        // Add proper event handling for better responsiveness
        suppressRowClickSelection={false}
        suppressCellSelection={false}
        // Optimize performance to prevent ResizeObserver issues
        enableCellTextSelection={false}
        suppressRowTransform={false}
        suppressAnimationFrame={false}
        // Enable immediate editing
        suppressDoubleClickExpand={true}
        // Ensure proper focus management
        stopEditingWhenCellsLoseFocus={true}
        {...rest}
      />
    </div>
  );
};

export default AgGridTable;
